/* eslint-disable no-param-reassign */

import distance from '@turf/distance';
import windowMatchMedia from '@Front/constants/windowMatchMedia';
import {
  Map as MapboxMap,
  NavigationControl,
  LngLat,
  LngLatBounds,
} from 'mapbox-gl';
import Component from '@bythepixel/component-loader';
import { createUserPin } from '@Core/components/locationSearch/utility';
import { mapboxMapConfig } from './config';
import Events from './events';
import GeolocatorEvents from '../geolocator/events';
import { getOffset } from './utility';

export default class _Map extends Component {
  static selector = '[data-comp-map]';

  bounds;
  map;
  geolocateByDefault;
  mapResizeInterval;

  mapLoaded = false;
  markers = new Map();

  constructor(...args) {
    super(...args);
    this.mapResizeInterval = parseInt(
      this.$container.dataset.compMapResizeInterval,
    );
    this.bounds = new LngLatBounds();
    this.scrollZoomDisabled = this.$container.dataset.compMapDisableScrollZoom === 'true' || false;
    this.map = this.#buildMap();
    this.#initMapFeatures();
    this.geolocateByDefault = this.$container.hasAttribute(
      'data-geolocate-by-default',
    );

    this.mount();
  }

  addMarker(marker) {
    if (!this.markers.has(marker.id)) {
      this.bounds.extend(marker.coordinates());
      this.markers.set(marker.id, marker);
    }

    this.markers.get(marker.id).addTo(this.map);
  }

  calcDistances() {
    const options = { units: 'miles' };
    const from = this.markers.get('user').point;

    // Get distance to each location from the user's geolocation
    [...this.markers.values()].forEach((marker) => {
      if (marker.id === 'user') return;

      const to = marker.point;

      marker.distanceFromUser = distance(from, to, options);
    });
  }

  refreshBounds() {
    this.bounds = new LngLatBounds();

    [...this.markers.values()].forEach((marker) => {
      this.bounds.extend(marker.coordinates());
    });

    // On <group>/parts and <group>/services, the listings container will sometimes
    // drastically change in height after a filter is selected. the map doesn't always
    // resize correctly to fit the container. This fires off a resize() 5 times over
    // some period in MS defined by mapResizeInterval
    if (this.mapResizeInterval) {
      this.fireResizes();
      return;
    }

    this.map.fitBounds(this.bounds, { padding: 30, maxZoom: 10 });
  }

  fireResizes() {
    let maxResizes = 3;

    const interval = setInterval(() => {
      if (maxResizes === 0) {
        clearInterval(interval);
        this.map.fitBounds(this.bounds, { padding: 30, maxZoom: 10 });
        return;
      }

      maxResizes -= 1;
      this.map.resize();
    }, this.mapResizeInterval);
  }

  flyTo(mapMarker, options = {}) {
    const zoom = options.zoom || this.map.getZoom();
    const { lng, lat } = mapMarker.coordinates();

    const center = [lng, lat];

    // The offset for the latitude needs to be less the more zoomed in it is so
    // the popup remains in view on mobile
    const offset = getOffset(zoom);
    center[1] += offset / zoom;

    this.map.flyTo({
      center,
      padding: 100,
      zoom,
      speed: 3,
    });
  }

  on(event, cb) {
    this.map.on(event, cb);
  }

  closeActivePopup() {
    if (!this.markerWithActivePopup) return;

    this.markerWithActivePopup.closePopup();
  }

  toggleMapFeatures() {
    if (!windowMatchMedia['break-3'].matches) {
      this.map.dragPan.disable();
      this.map.scrollZoom.disable();
      this.map.touchZoomRotate.disable();

      return;
    }

    this.map.dragPan.enable();
    if (!this.scrollZoomDisabled) this.map.scrollZoom.enable();
    this.map.touchZoomRotate.enable();
  }

  #initMapFeatures() {
    if (this.scrollZoomDisabled) {
      this.map.scrollZoom.disable();
    }

    // Add zoom, rotation controls
    this.map.addControl(new NavigationControl());

    // Toggle certain map functionality on mobile for map embeds (not location search)
    if (document.querySelector('[data-comp-map-embed]')) {
      windowMatchMedia['break-3'].addListener(() => {
        this.toggleMapFeatures();
      });

      this.toggleMapFeatures();
    }
  }

  attachListeners() {
    this.map.on('load', () => {
      window.dispatchEvent(Events.MapLoaded(this));

      if (this.geolocateByDefault) {
        window.dispatchEvent(GeolocatorEvents.Geolocate);
      }
    });

    window.addEventListener('geolocate-success', (e) => {
      const geolocationData = e.detail;
      const { latitude, longitude } = geolocationData.coords;
      const userPin = createUserPin(longitude, latitude);

      this.addMarker(userPin);
      window.dispatchEvent(Events.UserPinAdded);
    });

    window.addEventListener('geolocate-disabled', () => {
      this.refreshBounds();
    });
  }

  #buildMap() {
    const initialZoom = windowMatchMedia['break-3'].matches ? 4 : 2.4;

    return new MapboxMap({
      ...mapboxMapConfig,
      container: this.$container,
      zoom: initialZoom,
    });
  }

  mount() {
    this.attachListeners();
  }
}
